import { dungApiThat, goiApi, goiApiKemEtag } from '../api/http';
import type { ApiError } from '../errors';
import { mayChuPhongBan } from '../../mock/phong-ban';
import { mockRequest } from '../../mock/transport';
import type {
  Department,
  DepartmentCoPhienBan,
  DepartmentCreate,
  DepartmentPage,
  DepartmentUpdate,
} from '../../modules/co-cau-to-chuc/types';

const GOC = '/api/v1/departments';

const duongDan = (id: string) => `${GOC}/${encodeURIComponent(id)}`;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/** Thiếu mảng `items` là máy chủ trả sai hợp đồng: báo lỗi máy chủ, đừng để view vỡ. */
const kiemTrang = (ds: DepartmentPage): DepartmentPage => {
  if (!Array.isArray(ds?.items)) {
    throw { code: 'INTERNAL', status: 200, message: '' } satisfies ApiError;
  }
  return ds;
};

const TRANG_RONG: DepartmentPage = { items: [], next_cursor: null };

/**
 * Truy cập cây phòng ban (G2-D1-17, ADR-0014, ADR-0067) qua Gateway.
 * `tenant_id` lấy từ token, không gửi tham số.
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/phong-ban.ts`).
 */
export const phongBanRepository = {
  /**
   * `GET /api/v1/departments` — một trang, sắp `(created_at, id)`. Màn lọc ở
   * client, nên `?mock=filtered-empty` trả đủ dữ liệu và rỗng-vì-lọc hiện ra
   * khi gõ ô tìm.
   */
  danhSach: ({ limit, cursor }: { limit: number; cursor?: string }): Promise<DepartmentPage> => {
    if (dungApiThat()) return goiApi<DepartmentPage>(GOC, { query: { limit, cursor } }).then(kiemTrang);
    return mockRequest(mayChuPhongBan.danhSach({ limit, cursor }), TRANG_RONG);
  },

  /** `GET /api/v1/departments/{id}` kèm `ETag` — hộp sửa đọc trước khi ghi. */
  chiTiet: async (id: string): Promise<DepartmentCoPhienBan> => {
    if (!dungApiThat()) return giaLap(() => mayChuPhongBan.chiTiet(id));
    const { du, etag } = await goiApiKemEtag<Department>(duongDan(id));
    return { ...du, etag };
  },

  /** `POST /api/v1/departments` — `khoa` là `Idempotency-Key`, mới cho mỗi lần bấm. */
  tao: async (du: DepartmentCreate, khoa: string): Promise<DepartmentCoPhienBan> => {
    if (!dungApiThat()) return giaLap(() => mayChuPhongBan.tao(du));
    const kq = await goiApiKemEtag<Department>(GOC, { method: 'POST', body: du, idempotencyKey: khoa });
    return { ...kq.du, etag: kq.etag };
  },

  /** `PUT /api/v1/departments/{id}` với `If-Match` — `412` khi người khác vừa đổi. */
  sua: async (id: string, du: DepartmentUpdate, etag: string): Promise<DepartmentCoPhienBan> => {
    if (!dungApiThat()) return giaLap(() => mayChuPhongBan.sua(id, du, etag));
    const kq = await goiApiKemEtag<Department>(duongDan(id), { method: 'PUT', body: du, ifMatch: etag });
    return { ...kq.du, etag: kq.etag };
  },

  /** `DELETE /api/v1/departments/{id}` → `204`; `409` khi còn người hoặc còn phòng con. */
  xoa: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(duongDan(id), { method: 'DELETE' }) : giaLap(() => mayChuPhongBan.xoa(id)),
};
